import { APP_INITIALIZER, NgModule } from "@angular/core";
import { firstValueFrom, of } from "rxjs";
import { catchError } from "rxjs/operators";
import { AuthService } from "./services/auth.service";
import { UtilsService } from "./services/utils.service";
import { BrowserModule } from "@angular/platform-browser";

import { AppComponent } from "./app.component";
import { fr_FR, NZ_I18N } from "ng-zorro-antd/i18n";
import { registerLocaleData } from "@angular/common";
import fr from "@angular/common/locales/fr";
import { FormsModule } from "@angular/forms";
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { JwtInterceptor } from "./core/helpers/jwt.interceptor";
import { ImpersonationInterceptor } from "./core/helpers/impersonation.interceptor";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NzMessageService } from "ng-zorro-antd/message";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { NzModalService } from "ng-zorro-antd/modal";
import { NzDrawerService } from "ng-zorro-antd/drawer";
import { NzImageService } from "ng-zorro-antd/image";
import { ngZorroConfig } from "./core/ngZorroConfig";
import { NZ_CONFIG } from "ng-zorro-antd/core/config";
import { RouterModule } from "@angular/router";
import { AppRoutes } from "./app.routing";
import { ToastrModule } from "ngx-toastr";
import { NzFormModule } from "ng-zorro-antd/form";
import { ImageCropperModule } from "ngx-image-cropper";
import { NzDividerModule } from "ng-zorro-antd/divider";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzSpinModule } from "ng-zorro-antd/spin";
import { LayoutsModule } from "./layouts/layouts.module";

registerLocaleData(fr);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  exports: [],
  imports: [
    BrowserModule,
    RouterModule.forRoot(AppRoutes, {
      useHash: false,
      // Indispensable pour que le reload-current-route déclenché à la
      // bascule d'impersonation re-rentre bien dans le composant : sans
      // ce flag, navigate(currentUrl) est ignoré comme "déjà sur l'URL".
      onSameUrlNavigation: "reload",
    }),
    FormsModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    NzFormModule,
    ImageCropperModule,
    NzDividerModule,
    NzButtonModule,
    NzSpinModule,
    LayoutsModule,
  ],
  providers: [
    { provide: NZ_I18N, useValue: fr_FR },
    { provide: NZ_CONFIG, useValue: ngZorroConfig },
    NzMessageService,
    NzNotificationService,
    NzModalService,
    NzDrawerService,
    NzImageService,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ImpersonationInterceptor, multi: true },
    provideHttpClient(withInterceptorsFromDi()),
    {
      // Multi-tenant : si un token est présent au boot, charger le profil pour
      // hydrater le TenantContextService (isSuperAdmin / institutionId) avant
      // que l'app commence à router. Erreur (token expiré, backend down) →
      // on laisse l'app booter, les guards prendront le relais.
      provide: APP_INITIALIZER,
      useFactory: (auth: AuthService, utils: UtilsService) => () => {
        if (!utils.hasToken()) return Promise.resolve();
        return firstValueFrom(auth.getProfile().pipe(catchError(() => of(null))));
      },
      deps: [AuthService, UtilsService],
      multi: true,
    },
  ],
})
export class AppModule {}
